/**
 * Mixin dùng chung cho hai màn Báo cáo hoa hồng V2 (F-10 §7).
 *
 * NT-3 (§7.2): mọi hàm dùng lại đặt ở tầng dùng chung — cấm viết hàm thứ hai
 * cho cùng một việc. Report.vue và ReportDetail.vue chỉ chiếu lại dữ liệu,
 * không tự định dạng / tự tính lại theo cách riêng.
 */
import moment from 'moment';
import { formatDate, convertPrice } from '@/utils/common';
import {
  COMMISSION_REPORT_TYPE,
  COMMISSION_SOURCE_TYPE_NAME,
  COMMISSION_SOURCE_TYPE_VARIANT,
  COMMISSION_DIRECTION,
  MAX_REPORT_RANGE_DAYS,
} from './commission.constants';

export const DATE_INPUT_FORMAT = 'DD/MM/YYYY';
export const DATE_API_FORMAT = 'YYYY-MM-DD';

/** Cấu hình date-picker dùng chung cho cả hai màn */
export const DATE_PICKER_CONFIG = Object.freeze({
  format: DATE_INPUT_FORMAT,
  useCurrent: false,
  showClear: true,
  showClose: true,
  locale: 'vi',
});

/** Sinh thStyle chuẩn cho b-table — dùng lại ở cả hai màn */
export const thStyle = (width) => ({
  fontWeight: 600,
  color: '#181c32',
  width,
});

export default {
  data() {
    return {
      /** Loại báo cáo hiện tại, đọc từ ?type= */
      reportType: COMMISSION_REPORT_TYPE.STORE,
      dpConfigs: { date: DATE_PICKER_CONFIG },
    };
  },
  computed: {
    isCrReport() {
      return this.reportType === COMMISSION_REPORT_TYPE.CR;
    },
    /** Nhãn chiều lọc thay đổi theo loại báo cáo (§7.8) */
    scopeLabel() {
      return this.isCrReport ? 'Bộ phận' : 'Cửa hàng';
    },
  },
  methods: {
    /* ----------------------------- Loại báo cáo ---------------------------- */
    /** Đọc ?type= từ URL, mặc định là ngành cửa hàng */
    resolveReportType() {
      this.reportType =
        this.$route.query.type === COMMISSION_REPORT_TYPE.CR
          ? COMMISSION_REPORT_TYPE.CR
          : COMMISSION_REPORT_TYPE.STORE;
    },

    /* ------------------------------ Ngày tháng ----------------------------- */
    /** 'DD/MM/YYYY' (input) -> 'YYYY-MM-DD' (API); trả undefined nếu không hợp lệ */
    toApiDate(value) {
      if (!value) {
        return undefined;
      }
      const parsed = moment(value, DATE_INPUT_FORMAT, true);
      return parsed.isValid() ? parsed.format(DATE_API_FORMAT) : undefined;
    },
    /** 'YYYY-MM-DD' (API) -> 'DD/MM/YYYY' (hiển thị / input) */
    toInputDate(value) {
      if (!value) {
        return '';
      }
      const parsed = moment(value, DATE_API_FORMAT, true);
      return parsed.isValid()
        ? parsed.format(DATE_INPUT_FORMAT)
        : formatDate(value, DATE_INPUT_FORMAT) || '';
    },
    /** Hiển thị ngày ngắn gọn trong bảng */
    displayDate(value) {
      return value ? formatDate(value, DATE_INPUT_FORMAT) : '--';
    },
    /** Kỳ báo cáo dạng "01/07/2026 - 31/07/2026" */
    displayPeriod(period) {
      if (!period) {
        return '';
      }
      return `${this.toInputDate(period.fromDate)} - ${this.toInputDate(
        period.toDate,
      )}`;
    },
    /**
     * Kiểm tra khoảng ngày trước khi gọi API (§7.12).
     * Trả về chuỗi lỗi, hoặc null nếu hợp lệ.
     */
    validateDateRange(fromDate, toDate) {
      const from = moment(fromDate, DATE_INPUT_FORMAT, true);
      const to = moment(toDate, DATE_INPUT_FORMAT, true);
      if (!from.isValid() || !to.isValid()) {
        return 'Vui lòng chọn đầy đủ Từ ngày và Đến ngày!';
      }
      if (to.isBefore(from, 'day')) {
        return 'Đến ngày phải lớn hơn hoặc bằng Từ ngày!';
      }
      if (to.diff(from, 'days') + 1 > MAX_REPORT_RANGE_DAYS) {
        return `Khoảng thời gian xem báo cáo tối đa ${MAX_REPORT_RANGE_DAYS} ngày!`;
      }
      return null;
    },

    /* -------------------------------- Tiền ------------------------------- */
    /** Định dạng tiền, giữ dấu âm; 0 hiển thị '0' */
    displayAmount(value) {
      const amount = Number(value) || 0;
      if (amount < 0) {
        return `-${convertPrice(Math.abs(amount))}`;
      }
      return convertPrice(amount);
    },
    /** Định dạng số lượng (KHÔNG phải tiền — NT-4 §7.2) */
    displayQuantity(value) {
      return convertPrice(Number(value) || 0);
    },
    /** Tô đỏ khi số tiền âm */
    amountClass(value) {
      return Number(value) < 0 ? 'text-danger font-weight-bold' : '';
    },

    /* ------------------------------- Chiều -------------------------------- */
    /**
     * §7.4: billRows[].amount LUÔN DƯƠNG, phải nhân direction khi hiển thị.
     * categoryGroups / overview đã cộng dấu sẵn — KHÔNG dùng hàm này cho chúng.
     */
    signedAmount(row) {
      const amount = Number(row && row.amount) || 0;
      const direction =
        Number(row && row.direction) === COMMISSION_DIRECTION.MINUS
          ? COMMISSION_DIRECTION.MINUS
          : COMMISSION_DIRECTION.PLUS;
      return amount * direction;
    },
    isMinusRow(row) {
      return Number(row && row.direction) === COMMISSION_DIRECTION.MINUS;
    },

    /* ------------------------------- Nguồn -------------------------------- */
    sourceTypeName(row) {
      // BE đã trả sẵn nhãn ở sourceTypeName; chỉ tra bảng khi thiếu
      if (row && row.sourceTypeName) {
        return row.sourceTypeName;
      }
      return COMMISSION_SOURCE_TYPE_NAME[row && row.sourceType] || '--';
    },
    sourceTypeVariant(sourceType) {
      return COMMISSION_SOURCE_TYPE_VARIANT[sourceType] || 'label-light-info';
    },

    /* ------------------------------ Phạm vi ------------------------------- */
    /** Tên cửa hàng / bộ phận hiển thị trên đầu báo cáo */
    displayScopeName(report) {
      if (!report) {
        return '';
      }
      if (this.isCrReport) {
        return report.departmentName || '';
      }
      const store = report.store || {};
      return store.shortName || store.name || '';
    },

    /* ------------------------------- Lỗi API ------------------------------ */
    /**
     * Thông điệp lỗi từ response API, rơi về error.message rồi câu mặc định.
     * NT-3: mọi màn hoa hồng dùng chung hàm này — không khai lại trong component,
     * không viết inline `error.response && error.response.data && ...`.
     */
    getErrorMessage(error, defaultMessage) {
      return (
        (error && error.response && error.response.data
          ? error.response.data.message
          : null) ||
        (error && error.message) ||
        defaultMessage
      );
    },
  },
};
